import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BellRing, Check, MessageCircle, TriangleAlert } from "lucide-react";
import WhatsAppHealth from "@/components/admin/WhatsAppHealth";

const LABELS = {
  emergency: "possible emergency",
  pregnancy: "pregnancy / feeding",
  child: "a child",
  medication: "dosage or other medicines",
  condition: "an existing condition",
  human: "asked for a human",
  order: "order / payment problem",
  model: "needs a vaidya",
};

const CHANNEL_NOTE = {
  whatsapp: "The clinic is pinged on WhatsApp for every handover.",
  sms: "Alerts go out by SMS — add a Twilio WhatsApp sender to switch to WhatsApp.",
  none: "No WhatsApp/SMS sender on the Twilio account yet, so handovers only show up here.",
};

/**
 * Live queue of visitors Vaidya AI handed over. Polls every 30s and toasts new
 * arrivals, so somebody picks them up even if Twilio cannot deliver the ping.
 */
export const HandoverAlerts = () => {
  const [data, setData] = useState(null);
  const [destination, setDestination] = useState("");
  const seen = useRef(new Set());
  const primed = useRef(false);

  const load = useCallback(async () => {
    try {
      const { data: body } = await api.get("/admin/handovers", { params: { limit: 25 } });
      const fresh = body.handovers.filter((h) => !h.handled_at && !seen.current.has(h.id));
      body.handovers.forEach((h) => seen.current.add(h.id));
      if (primed.current) {
        fresh.forEach((h) =>
          toast[h.urgent ? "error" : "info"](
            h.urgent ? "Urgent handover — call them back" : "New Vaidya AI handover",
            { description: h.question.slice(0, 90) }
          )
        );
      }
      primed.current = true;
      setData(body);
    } catch (e) { /* the overview tab keeps working even if this poll fails */ }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, [load]);

  const saveSettings = async (patch) => {
    try {
      await api.put("/settings", patch);
      toast.success("Alert settings saved");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const markHandled = async (id) => {
    try {
      await api.post(`/admin/handovers/${id}/handled`);
      toast.success("Marked as handled");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!data) return null;
  const waRoot = (data.alerts.destination || "").replace(/\D/g, "");

  return (
    <Card className="border-border" data-testid="handover-alerts">
      <CardContent className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
          <h4 className="font-display text-2xl flex items-center gap-2">
            <BellRing size={17} /> Visitors waiting for a vaidya
          </h4>
          <Badge
            variant="outline"
            className={data.waiting > 0 ? "border-destructive/40 text-destructive bg-destructive/10" : "border-border"}
            data-testid="handover-waiting-count"
          >
            {data.waiting} waiting
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground mb-4" data-testid="handover-channel-note">
          {CHANNEL_NOTE[data.alerts.channel] || ""}
          {data.alerts.destination ? ` Alerts go to ${data.alerts.destination}.` : ""}
          {data.alerts.last_status === "failed" && data.alerts.last_detail
            ? ` Last attempt failed: ${data.alerts.last_detail}`
            : ""}
        </p>

        <WhatsAppHealth onChange={load} />

        <div className="flex flex-wrap items-end gap-3 mb-6 pb-5 border-b border-border">
          <div>
            <label className="overline">Alert this number</label>
            <Input
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              placeholder={data.alerts.destination || "+9198…"}
              className="mt-2 bg-transparent w-48"
              data-testid="handover-alert-number"
            />
          </div>
          <Button
            variant="outline"
            className="btn-pill h-10"
            onClick={() => saveSettings({ handover_alert_number: destination })}
            data-testid="handover-alert-save"
          >
            Save number
          </Button>
          <Button
            variant="ghost"
            className="btn-pill h-10"
            onClick={() => saveSettings({ handover_alerts_enabled: !data.alerts.enabled })}
            data-testid="handover-alert-toggle"
          >
            {data.alerts.enabled ? "Pause alerts" : "Resume alerts"}
          </Button>
        </div>

        {data.handovers.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="handover-empty">
            Nobody is waiting — Vaidya AI is handling the questions coming in.
          </p>
        )}

        <div className="space-y-2">
          {data.handovers.map((h) => (
            <div
              key={h.id}
              className={`rounded-xl border p-4 ${h.handled_at ? "border-border opacity-60" : h.urgent ? "border-destructive/50 bg-destructive/5" : "border-primary/30 bg-primary/5"}`}
              data-testid={`handover-row-${h.id}`}
            >
              <div className="flex flex-wrap items-center gap-2">
                {h.urgent && <TriangleAlert size={14} className="text-destructive" />}
                <Badge variant="outline" className="text-[10px] capitalize">{LABELS[h.reason] || h.reason}</Badge>
                <span className="text-xs text-muted-foreground">{h.user_type}</span>
                <span className="text-xs text-muted-foreground ml-auto">
                  {new Date(h.created_at).toLocaleString()}
                </span>
              </div>
              <p className="text-sm mt-2">{h.question}</p>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                {waRoot && (
                  <a
                    href={`https://wa.me/${waRoot}?text=${encodeURIComponent(`Namaste! Following up on your question: ${h.question.slice(0, 200)}`)}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`handover-wa-${h.id}`}
                  >
                    <Button variant="outline" size="sm" className="btn-pill h-9">
                      <MessageCircle size={13} className="mr-1.5" /> Open WhatsApp
                    </Button>
                  </a>
                )}
                {!h.handled_at && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="btn-pill h-9"
                    onClick={() => markHandled(h.id)}
                    data-testid={`handover-handled-${h.id}`}
                  >
                    <Check size={13} className="mr-1.5" /> Mark handled
                  </Button>
                )}
                {h.alert?.status && (
                  <span className="text-[11px] text-muted-foreground ml-auto">
                    alert: {h.alert.channel} · {h.alert.status}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default HandoverAlerts;
